/**
 * Pollinations AI BYOP (Bring Your Own Pollen) Authentication Service
 * Implements OAuth 2.0 PKCE flow for user-provided API key storage.
 */

const POLLINATIONS_AUTH_URL = 'https://enter.pollinations.ai/authorize';
const STORAGE_KEY_USER_TOKEN = 'pollinations_user_token';
const STORAGE_KEY_CODE_VERIFIER = 'pollinations_pkce_verifier';

/**
 * Generate a random code verifier for PKCE
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate code challenge from verifier using SHA-256
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64-url encode a buffer
 */
function base64UrlEncode(buffer: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Get the redirect URL for OAuth callback
 */
function getRedirectUrl(): string {
  return window.location.origin + '/auth/callback';
}

/**
 * Initiate the BYOP OAuth PKCE login flow.
 * Redirects the user to Pollinations authorization page.
 */
export async function startLoginFlow(appKey: string): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  sessionStorage.setItem(STORAGE_KEY_CODE_VERIFIER, codeVerifier);

  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const redirectUrl = getRedirectUrl();

  const params = new URLSearchParams({
    redirect_url: redirectUrl,
    client_id: appKey,
  });

  window.location.href = `${POLLINATIONS_AUTH_URL}?${params.toString()}`;
}

/**
 * Handle the OAuth callback redirect.
 * Extracts the API key from URL fragment or query parameter.
 * Stores the key in localStorage and cleans the URL.
 * Returns the user's API key or null if not found.
 */
export function handleOAuthCallback(): string | null {
  let key: string | null = null;

  // Check URL fragment first (#key=... or #access_token=... etc.)
  const hash = window.location.hash;
  if (hash) {
    const hashParams = new URLSearchParams(hash.substring(1));
    key = hashParams.get('key')
      || hashParams.get('token')
      || hashParams.get('api_key')
      || hashParams.get('access_token')
      || hashParams.get('sk');
  }

  // Check query parameters (?key=... or ?token=... etc.)
  if (!key) {
    const urlParams = new URLSearchParams(window.location.search);
    key = urlParams.get('key')
      || urlParams.get('token')
      || urlParams.get('api_key')
      || urlParams.get('access_token')
      || urlParams.get('sk')
      || urlParams.get('code');
  }

  // Also try to extract key from the raw URL if it contains sk_ pattern
  if (!key) {
    const rawUrl = window.location.href;
    const skMatch = rawUrl.match(/(sk_[a-zA-Z0-9_-]+)/);
    if (skMatch) {
      key = skMatch[1];
    }
  }

  if (key) {
    setUserToken(key);
    // Clean the URL
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  // Clean up PKCE verifier
  sessionStorage.removeItem(STORAGE_KEY_CODE_VERIFIER);

  return key;
}

/**
 * Store the user's Pollinations API key in localStorage
 */
export function setUserToken(token: string): void {
  localStorage.setItem(STORAGE_KEY_USER_TOKEN, token);
}

/**
 * Get the stored user API key
 */
export function getUserToken(): string | null {
  return localStorage.getItem(STORAGE_KEY_USER_TOKEN);
}

/**
 * Check if user is authenticated (has a stored token)
 */
export function isAuthenticated(): boolean {
  const token = getUserToken();
  return token !== null && token.length > 0;
}

/**
 * Check if the stored token is a valid Pollinations API key
 */
export function isValidPollinationsKey(): boolean {
  const token = getUserToken();
  return token !== null && token.startsWith('sk_');
}

/**
 * Logout / disconnect the user's Pollinations account
 */
export function logout(): void {
  localStorage.removeItem(STORAGE_KEY_USER_TOKEN);
  sessionStorage.removeItem(STORAGE_KEY_CODE_VERIFIER);
}

/**
 * Get the masked version of a token for display (e.g., "sk_...abc123")
 */
export function getMaskedToken(token: string): string {
  if (token.length <= 8) return '***';
  return token.substring(0, 3) + '...' + token.substring(token.length - 6);
}
